import React from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';

import { tokens } from '@hotjar/hotsauce';

const ACTIVE_SECTION_HEIGHT = 8;
const INACTIVE_SECTION_HEIGHT = 3;

const GAP_SIZE = 1.5;
// We can't show a fix 3px gap for sections that are smaller than 3px
// so we only use gap for bigger sections
const MIN_SIZE_FOR_SECTION_WITH_GAP = 5 * GAP_SIZE;

const lastSectionStyle = css`
  border-top-right-radius: 4px;
  border-bottom-right-radius: 4px;
`;

const lastSectionOfPageStyle = css`
  padding-right: ${GAP_SIZE}px;
`;

const firstSectionStyle = css`
  border-top-left-radius: 4px;
  border-bottom-left-radius: 4px;
`;

const SectionDiv = styled.div<{
  isFirst: boolean;
  isLast: boolean;
  isSmallSection: boolean;
  isSectionWithActivity: boolean;
  isBeforeInactiveSection: boolean;
  isAfterInactiveSection: boolean;
  isLastSectionOfPage: boolean;
}>`
  background: ${tokens.color.neutral_500};
  cursor: pointer;
  background-clip: content-box;
  box-sizing: border-box;
  position: absolute;
  height: ${({ isSectionWithActivity }) =>
    isSectionWithActivity ? ACTIVE_SECTION_HEIGHT : INACTIVE_SECTION_HEIGHT}px;
  top: ${({ isSectionWithActivity }) =>
    isSectionWithActivity ? 0 : (ACTIVE_SECTION_HEIGHT - INACTIVE_SECTION_HEIGHT) / 2}px;
  transform-origin: center;

  ${({ isFirst, isLast, isSectionWithActivity, isSmallSection }) => {
    if (!isSectionWithActivity || isSmallSection) {
      return '';
    }
    if (isFirst) {
      return firstSectionStyle;
    }

    if (isLast) {
      return lastSectionStyle;
    }

    return '';
  }};

  ${({ isLastSectionOfPage, isLast }) => {
    if (isLastSectionOfPage && !isLast) {
      return lastSectionOfPageStyle;
    }

    return '';
  }};

  overflow: hidden;

  transition: all 0.1s ease-out;
`;

const ProgressBarDiv = styled.div<{
  isSectionWithActivity: boolean;
}>`
  background: ${tokens.color.ember_600};
  height: ${({ isSectionWithActivity }) => (isSectionWithActivity ? 8 : 3)}px;
  width: 100%;
  transform-origin: top left;
`;

const getScaleY = (isExpanded: boolean, isAtLeastHalfExpanded: boolean): number => {
  if (isExpanded) {
    return 2;
  }
  if (isAtLeastHalfExpanded) {
    return 1.5;
  }
  return 1;
};

interface Props {
  isFirst: boolean;
  isLast: boolean;
  xPositionInPx: number;
  xWidthInPx: number;
  xProgressInPercent: number;
  onMouseDown: (clientX: number, uiPosition: string) => void;
  isHoveringOnSection: boolean;
  isAtLeastHalfExpanded: boolean;
  isSectionWithActivity: boolean;
  isBeforeInactiveSection: boolean;
  isAfterInactiveSection: boolean;
  isLastSectionOfPage: boolean;
}

const SectionUnmemo = ({
  isFirst,
  isLast,
  xPositionInPx,
  xWidthInPx,
  xProgressInPercent,
  onMouseDown,
  isHoveringOnSection,
  isAtLeastHalfExpanded,
  isSectionWithActivity,
  isBeforeInactiveSection,
  isAfterInactiveSection,
  isLastSectionOfPage,
}: Props): JSX.Element => {
  return (
    <SectionDiv
      isFirst={isFirst}
      isLast={isLast}
      isBeforeInactiveSection={isBeforeInactiveSection}
      isAfterInactiveSection={isAfterInactiveSection}
      isSectionWithActivity={isSectionWithActivity}
      isSmallSection={xWidthInPx < MIN_SIZE_FOR_SECTION_WITH_GAP}
      isLastSectionOfPage={isLastSectionOfPage}
      onMouseDown={(e) => onMouseDown(e.clientX, 'clicked_directly_on_timeline')}
      draggable="false"
      data-testid="timeline-section"
      style={{
        left: xPositionInPx,
        width: xWidthInPx,
        transform: `scaleY(${getScaleY(isHoveringOnSection, isAtLeastHalfExpanded)})`,
      }}
    >
      <ProgressBarDiv
        draggable="false"
        isSectionWithActivity={isSectionWithActivity}
        style={{
          transform: `scaleX(${xProgressInPercent / 100})`,
        }}
      />
    </SectionDiv>
  );
};

export const Section = React.memo(SectionUnmemo);
